import React from 'react';
import PropTypes from 'prop-types';
import { Box, Text, RadioButton } from 'grommet';
import {
  FormPrevious, FormNext, FormClose, Download,
} from 'grommet-icons';
import uniqid from 'uniqid';
import Papa from 'papaparse';
import { AppButton } from '@Components/Control';
import { useAppTheme } from '@Components/Context';
import { ProductOfferingType } from '@RedflagAI/node-utility/interface';
import {
  ProductFormSingleTextInput,
  ProductFormDropdownSelect,
  ProductFormCheckBoxInput,
} from '@Components/Partial/Product/NewSearch';
import { isValidYouTubeChannelUrl } from '@Helpers/util';

const SOCIAL_PLATFORMS = [
  { id: 'tiktok', label: 'TikTok' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'dailymotion', label: 'Dailymotion' },
  { id: 'telegram', label: 'Telegram' },
  { id: 'youtube', label: 'YouTube' },
  { id: 'twitter', label: 'X (Twitter)' },
  { id: 'pinterest', label: 'Pinterest' },
  { id: 'reddit', label: 'Reddit' },
  { id: 'soundcloud', label: 'SoundCloud' },
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'blogspot', label: 'Blogspot' },
  { id: 'quora', label: 'Quora' },
  { id: 'tumblr', label: 'Tumblr' },
  { id: 'disqus', label: 'Disqus' },
  { id: 'vk', label: 'VK' },
];

// --- Helpers ---
const buildCidPlanTemplates = (pricingData) => {
  const rules = pricingData?.rules?.youtubeCid;
  const planLabels = pricingData?.display?.planLabels || {};
  const uploaderLabels = pricingData?.display?.uploaderLabels || {};
  if (!rules) return [];
  const templates = [];
  ['self', 'redflag'].forEach((uploaderKey) => {
    const planTypes = rules[uploaderKey];
    if (!planTypes) return;
    Object.entries(planTypes).forEach(([planKey, planData]) => {
      templates.push({
        key: `${uploaderKey}-${planKey}`,
        label: `${planLabels[planKey] || planKey} — ${uploaderLabels[uploaderKey] || uploaderKey}`,
        uploaderKey,
        planKey,
        data: planData,
      });
    });
  });
  return templates;
};

const templateToFormData = (template) => {
  const d = template.data;
  return {
    uploader: template.uploaderKey,
    planType: template.planKey,
    setupFeeAmount: d.setupFeeAmount ?? '',
    monthlyFeeAmount: d.monthlyFeeAmount ?? '',
    revenueSharePercent: d.revenueSharePercent ?? '',
    revenueShareStartMonth: d.revenueShareStartMonth ?? 0,
  };
};

const formatTemplatePreview = (template) => {
  const d = template.data;
  const setup = `Setup $${d.setupFeeAmount ?? 0}`;
  const monthly = `$${d.monthlyFeeAmount ?? 0}/mo`;
  const share = `${d.revenueSharePercent ?? 0}%`;
  const startMonth = d.revenueShareStartMonth ?? 0;
  if (startMonth > 0) {
    return `${setup} · ${monthly} · ${share} after month ${startMonth}`;
  }
  return `${setup} · ${monthly} · ${share} rev share`;
};

// --- Shared sub-components ---

const FieldLabel = ({
  label, required = false, theme,
}) => (
  <Box direction="row" gap="0.2rem" align="center" margin={{ bottom: '0.2rem' }}>
    <Text size="0.82rem" weight={600} color={theme.page.primaryText}>{label}</Text>
    {required && <Text size="0.75rem" color="var(--app-error)">*</Text>}
  </Box>
);

FieldLabel.propTypes = {
  label: PropTypes.string.isRequired,
  required: PropTypes.bool,
  theme: PropTypes.object.isRequired,
};

const NumberInput = ({
  value,
  onChange,
  min = 0,
  max = undefined,
  placeholder = '0',
}) => (
  <ProductFormSingleTextInput
    type="number"
    value={value}
    handleFormValues={onChange}
    focusHighlight="var(--page-focus-highlight)"
    min={min}
    max={max}
    placeholder={placeholder}
  />
);

NumberInput.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func.isRequired,
  min: PropTypes.number,
  max: PropTypes.number,
  placeholder: PropTypes.string,
};

// --- Config forms ---

const MAX_CID_CHANNELS_AGENCY = 100;
const MAX_CID_CHANNELS_INDIVIDUAL = 2;

const CidConfigForm = ({
  small, config, onUpdate, theme, maxChannels,
}) => {
  const { page, button } = theme;
  const [csvError, setCsvError] = React.useState('');

  const channels = config.channels || [''];
  const filledChannels = channels.filter((c) => c.trim());
  const atLimit = filledChannels.length >= maxChannels;

  const setChannel = (idx, val) => {
    const next = [...channels];
    next[idx] = val;
    onUpdate({ ...config, channels: next });
  };

  const addChannel = () => {
    if (atLimit) return;
    onUpdate({ ...config, channels: [...channels, ''] });
  };

  const removeChannel = (idx) => {
    const next = channels.filter((_, i) => i !== idx);
    onUpdate({ ...config, channels: next.length ? next : [''] });
  };

  const handleDownloadSampleCsv = () => {
    const csvContent = 'channels\nhttps://www.youtube.com/@example';
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'sample_channels.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCsvChange = (e) => {
    const file = e.target.files[0];
    if (!file) { setCsvError(''); return; }
    setCsvError('');

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        try {
          if (!results.data || results.data.length === 0) {
            setCsvError('CSV file must contain at least one row of data.');
            return;
          }

          const extracted = results.data
            .map((row) => {
              const val = row.channels || row.Channels || row.CHANNELS
                || row.channel || row.Channel || row.CHANNEL;
              return val ? String(val).trim() : null;
            })
            .filter(Boolean);

          if (extracted.length === 0) {
            setCsvError('No channels found. Ensure the CSV has a "channels" column.');
            return;
          }

          const invalid = extracted.filter((ch) => !isValidYouTubeChannelUrl(ch));
          if (invalid.length > 0) {
            setCsvError(`Invalid channel URL in CSV: ${invalid[0]}`);
            return;
          }

          const currentFilled = channels.filter((c) => c.trim());
          const combined = [...currentFilled.map((c) => c.trim()), ...extracted];
          const unique = new Set(combined);
          if (unique.size < combined.length) {
            setCsvError('CSV contains duplicate channels or channels already added.');
            return;
          }

          if (combined.length > maxChannels) {
            setCsvError(
              `Adding these ${extracted.length} channels would exceed the ${maxChannels} channel limit (currently at ${currentFilled.length}).`,
            );
            return;
          }

          onUpdate({ ...config, channels: [...combined, ''] });
          // eslint-disable-next-line no-param-reassign
          e.target.value = '';
        } catch (err) {
          setCsvError(err.message || 'Error parsing CSV file.');
        }
      },
      error: () => setCsvError('Invalid CSV format.'),
    });
  };

  return (
    <Box gap="0.75rem">
      <Box gap="0.5rem">
        <Box direction="row" justify="between" align="center" margin={{ bottom: '0.2rem' }}>
          <Box direction="row" gap="0.2rem" align="center">
            <Text size="0.82rem" weight={600} color={page.primaryText}>YouTube Channel URLs</Text>
            <Text size="0.75rem" color="var(--app-error)">*</Text>
          </Box>
          <Text size="0.75rem" color={atLimit ? 'var(--app-error)' : page.secondaryText}>
            {`${filledChannels.length} / ${maxChannels} channels`}
          </Text>
        </Box>
        {channels.map((ch, idx) => {
          const invalid = ch.trim() && !isValidYouTubeChannelUrl(ch.trim());
          return (
            // eslint-disable-next-line react/no-array-index-key
            <Box key={idx} direction="row" gap="0.5rem" align="center">
              <Box flex>
                <ProductFormSingleTextInput
                  small={small}
                  focusHighlight={page.focusHighlight}
                  value={ch}
                  handleFormValues={(val) => setChannel(idx, val)}
                  placeholder="https://www.youtube.com/@ChannelName"
                />
              </Box>
              {channels.length > 1 && (
                <AppButton
                  overrideHover
                  level="tableControls"
                  bgColor="transparent"
                  color="var(--app-error)"
                  label="✕"
                  onClick={() => removeChannel(idx)}
                />
              )}
              {invalid && (
                <Text size="0.72rem" color="var(--app-error)">Invalid YouTube channel URL</Text>
              )}
            </Box>
          );
        })}
        {!atLimit && (
          <AppButton
            overrideHover
            level="tableControls"
            width="fit-content"
            bgColor={button.primaryBg}
            color={button.primaryText}
            label="+ Add channel"
            onClick={addChannel}
          />
        )}
        <Box gap="0.35rem" margin={{ top: '0.15rem' }}>
          <Box direction="row" gap="0.75rem" align="center">
            <Text size="0.78rem" color={page.secondaryText}>Or import from CSV:</Text>
            <AppButton
              overrideHover
              level="tableControls"
              bgColor="transparent"
              width="fit-content"
              color={button.primaryBg}
              label={(
                <Box direction="row" align="center" gap="0.3rem">
                  <Download size="0.9rem" />
                  <Text size="0.75rem" color={button.primaryBg}>Download sample</Text>
                </Box>
              )}
              onClick={handleDownloadSampleCsv}
            />
          </Box>
          <Box
            border={{ color: page.border, size: '1px' }}
            round="var(--app-radius)"
            pad={{ horizontal: '0.75rem', vertical: '0.5rem' }}
            direction="row"
            align="center"
            justify="between"
            style={{ position: 'relative', overflow: 'hidden' }}
          >
            <Text size="0.78rem" color={page.secondaryText}>
              CSV with a &ldquo;channels&rdquo; column
            </Text>
            <AppButton
              overrideHover
              level="tableControls"
              width="fit-content"
              bgColor={button.primaryBg}
              color={button.primaryText}
              label="Choose CSV..."
            />
            <input
              type="file"
              accept=".csv"
              onChange={handleCsvChange}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                opacity: 0,
                cursor: 'pointer',
              }}
            />
          </Box>
          {csvError && (
            <Text size="0.75rem" color="var(--app-error)">{csvError}</Text>
          )}
        </Box>
      </Box>
    </Box>
  );
};

CidConfigForm.propTypes = {
  small: PropTypes.bool.isRequired,
  config: PropTypes.object.isRequired,
  onUpdate: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired,
  maxChannels: PropTypes.number.isRequired,
};

const SocialConfigForm = ({
  small, config, onUpdate, theme,
}) => {
  const selected = config.platforms || [];

  const togglePlatform = (id) => {
    const next = selected.includes(id)
      ? selected.filter((p) => p !== id)
      : [...selected, id];
    onUpdate({
      ...config,
      platforms: next,
      mainPlatform: next.includes(config.mainPlatform) ? config.mainPlatform : '',
    });
  };

  return (
    <Box gap="0.75rem">
      <Box gap="0.25rem">
        <FieldLabel label="Platforms" required theme={theme} />
        <Box direction="row" wrap>
          {SOCIAL_PLATFORMS.map((p) => (
            <Box key={p.id} width="48%" margin="0.5rem">
              <ProductFormCheckBoxInput
                controlled
                value={selected.includes(p.id)}
                handleFormValues={() => togglePlatform(p.id)}
                label={p.label}
                labelColor={theme.page.primaryText}
                borderColor={theme.page.inputBorder}
                focusHighlight={theme.page.focusHighlight}
                checkmarkColor={theme.page.highlight}
              />
            </Box>
          ))}
        </Box>
      </Box>
      <Box gap="0.25rem">
        <FieldLabel label="Main Platform (optional)" theme={theme} />
        <ProductFormDropdownSelect
          selectable
          keepLabel
          simpleOptions={selected}
          handleFormValues={(val) => onUpdate({ ...config, mainPlatform: val })}
          value={config.mainPlatform || ''}
          label={config.mainPlatform || 'Select main platform...'}
          focusHighlight={theme.page.focusHighlight}
          disabled={selected.length === 0}
        />
      </Box>
      <Box gap="0.25rem">
        <FieldLabel label="Main Link (optional)" theme={theme} />
        <ProductFormSingleTextInput
          small={small}
          focusHighlight={theme.page.focusHighlight}
          value={config.mainLink || ''}
          handleFormValues={(val) => onUpdate({ ...config, mainLink: val })}
          placeholder="https://..."
        />
      </Box>
    </Box>
  );
};

SocialConfigForm.propTypes = {
  small: PropTypes.bool.isRequired,
  config: PropTypes.object.isRequired,
  onUpdate: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired,
};

// --- Plan data forms ---

const CidPlanDataForm = ({
  data, onUpdate, theme,
}) => (
  <Box gap="0.75rem">
    <Box gap="0.25rem">
      <FieldLabel label="Plan Uploader" required theme={theme} />
      <Box direction="row" gap="1.5rem">
        {['redflag', 'self'].map((val) => (
          <RadioButton
            key={val}
            name="cidPlanUploader"
            value={val}
            checked={data.uploader === val}
            onChange={() => onUpdate({ ...data, uploader: val })}
            label={(
              <Text size="0.875rem" color={theme.page.primaryText}>
                {val === 'redflag' ? 'Redflag' : 'Self'}
              </Text>
            )}
          />
        ))}
      </Box>
    </Box>
    <Box direction="row" gap="1rem" wrap>
      <Box flex gap="0.25rem">
        <FieldLabel label="Setup Fee ($)" theme={theme} />
        <NumberInput
          value={data.setupFeeAmount ?? ''}
          onChange={(v) => onUpdate({ ...data, setupFeeAmount: v })}
        />
      </Box>
      <Box flex gap="0.25rem">
        <FieldLabel label="Monthly Fee ($)" theme={theme} />
        <NumberInput
          value={data.monthlyFeeAmount ?? ''}
          onChange={(v) => onUpdate({ ...data, monthlyFeeAmount: v })}
        />
      </Box>
    </Box>
    <Box direction="row" gap="1rem" wrap>
      <Box flex gap="0.25rem">
        <FieldLabel label="Revenue Share (%)" theme={theme} />
        <NumberInput
          value={data.revenueSharePercent ?? ''}
          onChange={(v) => onUpdate({ ...data, revenueSharePercent: v })}
          max={100}
          placeholder="0-100"
        />
      </Box>
      <Box flex gap="0.25rem">
        <FieldLabel label="Revenue Share Start Month" theme={theme} />
        <NumberInput
          value={data.revenueShareStartMonth ?? ''}
          onChange={(v) => onUpdate({ ...data, revenueShareStartMonth: v })}
          placeholder="0"
        />
      </Box>
    </Box>
  </Box>
);

CidPlanDataForm.propTypes = {
  data: PropTypes.object.isRequired,
  onUpdate: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired,
};

const SocialPlanDataForm = ({
  data, onUpdate, platforms, defaultAmount, theme,
}) => {
  const defaultPerPlatform = data.defaultPerPlatformAmount ?? defaultAmount ?? 10;
  const overrides = data.platformOverrides || [];

  React.useEffect(() => {
    const synced = platforms.map((pid) => {
      const existing = overrides.find((o) => o.platform === pid);
      return existing || { platform: pid, amount: defaultPerPlatform };
    });
    if (JSON.stringify(synced) !== JSON.stringify(overrides)) {
      onUpdate({ ...data, platformOverrides: synced });
    }
  }, [platforms.join(',')]);

  const updateOverride = (pid, amount) => {
    const next = overrides.map((o) => (o.platform === pid ? { ...o, amount } : o));
    onUpdate({ ...data, platformOverrides: next });
  };

  return (
    <Box gap="0.75rem">
      <Box gap="0.25rem">
        <FieldLabel label="Default Per-Platform Amount ($)" required theme={theme} />
        <NumberInput
          value={defaultPerPlatform}
          onChange={(v) => onUpdate({ ...data, defaultPerPlatformAmount: parseFloat(v) || 0 })}
        />
      </Box>
      {platforms.length > 0 && (
        <Box gap="0.4rem">
          <Text size="0.82rem" weight={600} color={theme.page.primaryText}>
            Per-Platform Overrides
          </Text>
          {overrides.map((o) => (
            <Box key={o.platform} direction="row" align="center" gap="0.75rem">
              <Box width="140px">
                <Text size="0.82rem" color={theme.page.primaryText}>{o.platform}</Text>
              </Box>
              <Box width="120px">
                <NumberInput
                  value={o.amount}
                  onChange={(v) => updateOverride(o.platform, parseFloat(v) || 0)}
                />
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

SocialPlanDataForm.propTypes = {
  data: PropTypes.object.isRequired,
  onUpdate: PropTypes.func.isRequired,
  platforms: PropTypes.array.isRequired,
  defaultAmount: PropTypes.number,
  theme: PropTypes.object.isRequired,
};

const GenericPlanDataForm = ({
  small, data, onUpdate, theme,
}) => (
  <Box gap="0.75rem">
    <Box gap="0.25rem">
      <FieldLabel label="Setup Fee ($, optional)" theme={theme} />
      <ProductFormSingleTextInput
        type="number"
        small={small}
        focusHighlight={theme.page.focusHighlight}
        value={data.setupFee ?? ''}
        handleFormValues={(val) => onUpdate({ ...data, setupFee: val })}
        placeholder="Leave blank to omit"
        min={0}
      />
    </Box>
    <Box gap="0.25rem">
      <FieldLabel label="Billing Frequency" required theme={theme} />
      <ProductFormDropdownSelect
        selectable
        simpleOptions={['monthly', 'one_time']}
        handleFormValues={(val) => onUpdate({ ...data, frequency: val })}
        value={data.frequency || 'monthly'}
        focusHighlight={theme.page.focusHighlight}
      />
    </Box>
    <Box gap="0.25rem">
      <FieldLabel label="Amount ($)" required theme={theme} />
      <NumberInput
        value={data.amount ?? ''}
        onChange={(v) => onUpdate({ ...data, amount: v })}
      />
    </Box>
  </Box>
);

GenericPlanDataForm.propTypes = {
  small: PropTypes.bool.isRequired,
  data: PropTypes.object.isRequired,
  onUpdate: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired,
};

// --- CID plan template selector ---
const CidTemplateSelector = ({
  templates, selectedKey, onSelect, theme,
}) => {
  const { page, button } = theme;
  return (
    <Box gap="0.4rem">
      <Text size="0.78rem" color={page.secondaryText}>
        Select a plan template to pre-fill the form, or configure values manually below.
      </Text>
      <Box direction="row" wrap>
        {templates.map((t) => {
          const isSelected = selectedKey === t.key;
          return (
            <Box
              key={t.key}
              pad={{ horizontal: '0.75rem', vertical: '0.5rem' }}
              margin="0.25rem"
              round="var(--app-radius)"
              border={{ color: isSelected ? button.primaryBg : page.border, size: '1px' }}
              background={isSelected ? `${button.primaryBg}18` : 'transparent'}
              style={{ cursor: 'pointer', flex: '1 1 45%' }}
              onClick={() => onSelect(t)}
            >
              <Text size="0.82rem" weight={600} color={page.primaryText}>{t.label}</Text>
              <Text size="0.72rem" color={page.secondaryText}>{formatTemplatePreview(t)}</Text>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

CidTemplateSelector.propTypes = {
  templates: PropTypes.array.isRequired,
  selectedKey: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired,
};

// --- Discount section ---
const DiscountSection = ({
  small, productIndex, productKey, extraItems, setExtraItems, theme,
}) => {
  const { page, button } = theme;

  const thisDiscounts = extraItems.filter(
    (item) => item.kind === 'discount' && item.productIdx === productIndex,
  );

  const addDiscount = () => setExtraItems((prev) => [
    ...prev,
    {
      id: uniqid(),
      kind: 'discount',
      valueType: 'amount',
      value: '',
      description: '',
      frequency: 'monthly',
      productKey: productKey || null,
      productIdx: productIndex,
    },
  ]);

  const updateDiscount = (id, patch) => setExtraItems(
    (prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)),
  );

  const removeDiscount = (id) => setExtraItems(
    (prev) => prev.filter((item) => item.id !== id),
  );

  return (
    <Box gap="0.5rem">
      <Box direction="row" justify="between" align="center">
        <Text size="0.85rem" weight={600} color={page.primaryText}>Discount (optional)</Text>
        <AppButton
          overrideHover
          level="tableControls"
          width="fit-content"
          bgColor={button.primaryBg}
          color={button.primaryText}
          label="+ Add discount"
          onClick={addDiscount}
        />
      </Box>
      {thisDiscounts.length === 0 && (
        <Text size="0.78rem" color={page.secondaryText}>No discounts added for this product.</Text>
      )}
      {thisDiscounts.map((item) => {
        const isPercent = item.valueType === 'percent';
        return (
          <Box
            key={item.id}
            pad="0.75rem"
            gap="0.5rem"
            round="var(--app-radius)"
            border={{ color: page.border, size: '1px' }}
          >
            <Box direction="row" gap="0.5rem" wrap>
              <Box flex={{ min: '120px' }} gap="0.2rem">
                <Text size="0.78rem" weight={600} color={page.primaryText}>Value Type</Text>
                <ProductFormDropdownSelect
                  selectable
                  simpleOptions={['amount', 'percent']}
                  handleFormValues={(val) => updateDiscount(item.id, { valueType: val })}
                  value={item.valueType}
                  focusHighlight={page.focusHighlight}
                />
              </Box>
              <Box flex={{ min: '100px' }} gap="0.2rem">
                <Text size="0.78rem" weight={600} color={page.primaryText}>
                  {`Value ${isPercent ? '(%)' : '($)'}`}
                </Text>
                <ProductFormSingleTextInput
                  small={small}
                  type="number"
                  focusHighlight={page.focusHighlight}
                  value={item.value}
                  handleFormValues={(val) => updateDiscount(item.id, { value: val })}
                  placeholder="0"
                  min={0}
                  max={isPercent ? 100 : undefined}
                />
              </Box>
              <Box flex={{ min: '120px' }} gap="0.2rem">
                <Text size="0.78rem" weight={600} color={page.primaryText}>Frequency</Text>
                <ProductFormDropdownSelect
                  selectable
                  simpleOptions={['monthly', 'one_time']}
                  handleFormValues={(val) => updateDiscount(item.id, { frequency: val })}
                  value={item.frequency}
                  focusHighlight={page.focusHighlight}
                />
              </Box>
            </Box>
            <Box gap="0.2rem">
              <Box direction="row" gap="0.2rem">
                <Text size="0.78rem" weight={600} color={page.primaryText}>Description</Text>
                <Text size="0.78rem" color="var(--app-error)">*</Text>
              </Box>
              <Box direction="row" gap="0.5rem" align="center">
                <Box flex>
                  <ProductFormSingleTextInput
                    small={small}
                    focusHighlight={page.focusHighlight}
                    value={item.description}
                    handleFormValues={(val) => updateDiscount(item.id, { description: val })}
                    placeholder="e.g. Early bird discount"
                  />
                </Box>
                <AppButton
                  overrideHover
                  level="dynamicSmall"
                  bgColor="var(--app-error)"
                  color="white"
                  label={(
                    <Box direction="row" align="center" gap="0.25rem">
                      <Text size="1rem">Remove</Text>
                      <FormClose size="1rem" />
                    </Box>
                  )}
                  onClick={() => removeDiscount(item.id)}
                />
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

DiscountSection.propTypes = {
  small: PropTypes.bool.isRequired,
  productIndex: PropTypes.number.isRequired,
  productKey: PropTypes.string,
  extraItems: PropTypes.array.isRequired,
  setExtraItems: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired,
};

// --- Main component ---

const Step3PlansConfig = ({
  small,
  pricingData,
  selectedProducts,
  productConfigs,
  updateProductConfig,
  extraItems,
  setExtraItems,
  activeSubStep,
  setActiveSubStep,
  accountType,
}) => {
  const { theme } = useAppTheme();
  const { page, button } = theme;
  const total = selectedProducts.length;

  const maxCidChannels = accountType === 'agency'
    ? MAX_CID_CHANNELS_AGENCY
    : MAX_CID_CHANNELS_INDIVIDUAL;

  if (total === 0) {
    return <Text color={page.secondaryText}>No products selected. Go back to Step 2.</Text>;
  }

  const product = selectedProducts[activeSubStep];
  const config = productConfigs[activeSubStep] || {};
  const productType = product?.productType;
  const isNew = product?.mode === 'new';

  const catalogPlans = product?.catalogPlans || [];
  const planOptions = catalogPlans.map((p) => ({ label: p.name || p.label, value: p.id }));

  const cidTemplates = productType === ProductOfferingType.CID
    ? buildCidPlanTemplates(pricingData)
    : [];

  const socialDefaultAmount = pricingData?.rules?.socialScanning?.perPlatformMonthlyFeeAmount
    ?? 10;

  const isSocial = productType === ProductOfferingType.SOCIAL;

  const updateConfig = (newConf) => updateProductConfig(activeSubStep, {
    ...config, config: newConf,
  });
  const updateCustomPlan = (patch) => updateProductConfig(activeSubStep, {
    ...config,
    customPlan: { ...config.customPlan, ...patch },
  });
  const updateCustomPlanData = (data) => updateProductConfig(activeSubStep, {
    ...config,
    customPlan: { ...config.customPlan, data },
  });
  const setPlanMode = (mode) => updateProductConfig(activeSubStep, {
    ...config, planMode: mode, planId: '', planLabel: '', templateKey: undefined,
  });

  const selectCidTemplate = (template) => {
    updateProductConfig(activeSubStep, {
      ...config,
      planMode: 'custom',
      templateKey: template.key,
      customPlan: {
        name: template.label,
        description: config.customPlan?.description || '',
        data: templateToFormData(template),
      },
    });
  };

  // Social is always custom — no plan mode toggle
  const showCustomPlanForm = config.planMode === 'custom' || isNew || isSocial;

  return (
    <Box gap="1rem">
      <Box direction="row" align="center" justify="between">
        <Box>
          <Text size="1rem" weight={700} color={page.primaryText}>
            {`Configuring product ${activeSubStep + 1} of ${total}`}
          </Text>
          <Text size="0.875rem" color={page.secondaryText}>
            {product?.productLabel}
            {isNew && (
              <Text size="0.875rem" color={page.secondaryText}>{' (custom)'}</Text>
            )}
          </Text>
        </Box>
        {total > 1 && (
          <Box direction="row" gap="0.5rem">
            <AppButton
              overrideHover
              level="dynamicSmall"
              bgColor={page.altBg}
              color={page.primaryText}
              borderColor={button.border}
              style={{ border: `1px solid ${button.border}` }}
              disabled={activeSubStep === 0}
              onClick={() => setActiveSubStep(activeSubStep - 1)}
              label={(
                <Box direction="row" align="center" gap="0.25rem">
                  <FormPrevious size="1rem" />
                  <Text size="0.8rem">Prev</Text>
                </Box>
              )}
            />
            <AppButton
              overrideHover
              level="dynamicSmall"
              bgColor={page.altBg}
              color={page.primaryText}
              borderColor={button.border}
              style={{ border: `1px solid ${button.border}` }}
              disabled={activeSubStep === total - 1}
              onClick={() => setActiveSubStep(activeSubStep + 1)}
              label={(
                <Box direction="row" align="center" gap="0.25rem">
                  <Text size="0.8rem">Next</Text>
                  <FormNext size="1rem" />
                </Box>
              )}
            />
          </Box>
        )}
      </Box>
      {/* CID configuration */}
      {productType === ProductOfferingType.CID && (
        <Box pad="1rem" round="var(--app-radius)" border={{ color: page.border, size: '1px' }}>
          <Text size="0.85rem" weight={600} color={page.primaryText} margin={{ bottom: '0.75rem' }}>
            CID Configuration
          </Text>
          <CidConfigForm
            small={small}
            config={config.config || {}}
            onUpdate={updateConfig}
            theme={theme}
            maxChannels={maxCidChannels}
          />
        </Box>
      )}
      {/* Social configuration */}
      {isSocial && (
        <Box pad="1rem" round="var(--app-radius)" border={{ color: page.border, size: '1px' }}>
          <Text size="0.85rem" weight={600} color={page.primaryText} margin={{ bottom: '0.75rem' }}>
            Social Configuration
          </Text>
          <SocialConfigForm
            small={small}
            config={config.config || {}}
            onUpdate={updateConfig}
            theme={theme}
          />
        </Box>
      )}
      {/* Plan */}
      <Box
        pad="1rem"
        round="var(--app-radius)"
        border={{ color: page.border, size: '1px' }}
        gap="0.75rem"
      >
        <Text size="0.85rem" weight={600} color={page.primaryText}>Plan</Text>
        {/* CID: template selector */}
        {productType === ProductOfferingType.CID && cidTemplates.length > 0 && (
          <CidTemplateSelector
            templates={cidTemplates}
            selectedKey={config.templateKey}
            onSelect={selectCidTemplate}
            theme={theme}
          />
        )}
        {/* Non-CID, non-social with catalog plans: existing vs custom toggle */}
        {!isNew && !isSocial && productType !== ProductOfferingType.CID
          && planOptions.length > 0 && (
          <Box direction="row" gap="1.5rem">
            {['existing', 'custom'].map((mode) => (
              <RadioButton
                key={mode}
                name={`planMode-${activeSubStep}`}
                value={mode}
                checked={config.planMode === mode}
                onChange={() => setPlanMode(mode)}
                label={(
                  <Text size="0.875rem" color={page.primaryText}>
                    {mode === 'existing' ? 'Use existing plan' : 'Define custom plan'}
                  </Text>
                )}
              />
            ))}
          </Box>
        )}
        {/* Existing plan dropdown (non-CID, non-social) */}
        {config.planMode === 'existing' && planOptions.length > 0 && (
          <Box gap="0.25rem">
            <Box direction="row" gap="0.2rem">
              <Text size="0.82rem" weight={600} color={page.primaryText}>Select Plan</Text>
              <Text size="0.82rem" color="var(--app-error)">*</Text>
            </Box>
            <ProductFormDropdownSelect
              selectable
              inputConfig={{
                overrideOptions: planOptions.map((p) => ({ api: p.value, display: p.label })),
              }}
              handleFormValues={(val) => {
                const found = planOptions.find((p) => p.value === val);
                updateProductConfig(activeSubStep, {
                  ...config,
                  planId: val,
                  planLabel: found?.label || '',
                });
              }}
              value={config.planId || ''}
              focusHighlight={page.focusHighlight}
            />
          </Box>
        )}
        {/* Custom plan form */}
        {showCustomPlanForm && (
          <Box gap="0.75rem">
            <Box gap="0.25rem">
              <FieldLabel label="Plan Name" required theme={theme} />
              <ProductFormSingleTextInput
                small={small}
                focusHighlight={page.focusHighlight}
                value={config.customPlan?.name || ''}
                handleFormValues={(val) => updateCustomPlan({ name: val })}
                placeholder="e.g. Revenue Share — Agency"
              />
            </Box>
            <Box gap="0.25rem">
              <Text size="0.82rem" weight={600} color={page.primaryText}>
                Description (optional)
              </Text>
              <ProductFormSingleTextInput
                small={small}
                focusHighlight={page.focusHighlight}
                value={config.customPlan?.description || ''}
                handleFormValues={(val) => updateCustomPlan({ description: val })}
                placeholder="Optional description"
              />
            </Box>
            <Box>
              <Text
                size="0.82rem"
                weight={600}
                color={page.primaryText}
                margin={{ bottom: '0.5rem' }}
              >
                Plan Data
              </Text>
              {productType === ProductOfferingType.CID && (
                <CidPlanDataForm
                  data={config.customPlan?.data || {}}
                  onUpdate={updateCustomPlanData}
                  theme={theme}
                />
              )}
              {isSocial && (
                <SocialPlanDataForm
                  data={config.customPlan?.data || {}}
                  onUpdate={updateCustomPlanData}
                  platforms={config.config?.platforms || []}
                  defaultAmount={socialDefaultAmount}
                  theme={theme}
                />
              )}
              {(productType === ProductOfferingType.WATERMARKING
                || productType === ProductOfferingType.CUSTOM) && (
                <GenericPlanDataForm
                  small={small}
                  data={config.customPlan?.data || {}}
                  onUpdate={updateCustomPlanData}
                  theme={theme}
                />
              )}
            </Box>
          </Box>
        )}
        {/* No catalog plans fallback for watermarking/custom */}
        {config.planMode === 'existing' && planOptions.length === 0 && !isNew
          && !isSocial && productType !== ProductOfferingType.CID && (
          <Box direction="column" gap="1rem">
            <Text size="0.8rem" color={page.secondaryText}>
              No catalog plans found. Define a custom plan instead.
            </Text>
            <AppButton
              overrideHover
              level="tableControls"
              bgColor={button.primaryBg}
              color={button.primaryText}
              label="Switch to custom plan"
              onClick={() => setPlanMode('custom')}
            />
          </Box>
        )}
      </Box>
      {/* Discount */}
      <Box pad="1rem" round="var(--app-radius)" border={{ color: page.border, size: '1px' }}>
        <DiscountSection
          small={small}
          productIndex={activeSubStep}
          productKey={product?.productKey}
          extraItems={extraItems}
          setExtraItems={setExtraItems}
          theme={theme}
        />
      </Box>
    </Box>
  );
};

Step3PlansConfig.propTypes = {
  small: PropTypes.bool.isRequired,
  pricingData: PropTypes.object,
  selectedProducts: PropTypes.array.isRequired,
  productConfigs: PropTypes.array.isRequired,
  updateProductConfig: PropTypes.func.isRequired,
  extraItems: PropTypes.array.isRequired,
  setExtraItems: PropTypes.func.isRequired,
  activeSubStep: PropTypes.number.isRequired,
  setActiveSubStep: PropTypes.func.isRequired,
  accountType: PropTypes.string,
};

export default Step3PlansConfig;
